/**
 * RestaurantsDropdown — navbar entry for restaurant owners.
 *
 * iter132 (Jul 2026) — Owner spec: after consolidating Why NoFee +
 * Restaurant Benefits + Pricing into ONE page (/for-restaurants), the
 * dropdown collapses to the smallest possible surface: two clear
 * decisions for a visitor.
 *
 *   Restaurant Benefits    → /for-restaurants
 *   Create Account         → /onboarding/account   (existing wizard —
 *                             Step 1, "Create Your Account")
 *
 *   Sign In (button)       → onOpenAuth('login')   (existing modal)
 *
 * NO "Why NoFee" and NO "Pricing" items — that content now lives
 * inside Restaurant Benefits. Visual style is unchanged (mirrors
 * PartnersDropdown) so the two persona menus stay consistent.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Store,
  ChevronDown,
  BadgePercent,
  Rocket,
  LogIn,
} from 'lucide-react';

const buildItems = (t) => [
  {
    icon: BadgePercent,
    label: t('restaurants.nav.restaurant_benefits', 'Restaurant Benefits'),
    description: t(
      'restaurants.nav.restaurant_benefits_desc',
      'Why NoFee, all the benefits, savings calculator, and pricing — in one place.',
    ),
    href: '/for-restaurants',
    testId: 'restaurants-dropdown-item-benefits',
  },
  {
    icon: Rocket,
    label: t('restaurants.nav.create_account', 'Create Account'),
    description: t(
      'restaurants.nav.create_account_desc',
      'Start the existing onboarding — Account, Plan, Stripe, Go Live.',
    ),
    href: '/onboarding/account',
    testId: 'restaurants-dropdown-item-create-account',
  },
];

export default function RestaurantsDropdown({ onOpenAuth }) {
  const { t } = useTranslation();
  const ITEMS = buildItems(t);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSelect = (item) => {
    setOpen(false);
    navigate(item.href);
  };

  const handleSignIn = () => {
    setOpen(false);
    if (typeof onOpenAuth === 'function') {
      onOpenAuth('login');
    } else {
      navigate('/login');
    }
  };

  return (
    <div ref={wrapRef} className="relative hidden lg:inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid="navbar-restaurants-link"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all text-white/85 hover:text-gold-300"
      >
        <Store className="w-3.5 h-3.5" />
        {t('restaurants.nav.restaurants', 'Restaurants')}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          data-testid="restaurants-dropdown-menu"
          className="absolute top-[calc(100%+10px)] right-0 w-[380px] rounded-2xl border border-white/[0.08] bg-gradient-to-b from-zinc-950 to-black shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)] p-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150"
        >
          {/* Section 1 — For New Restaurants */}
          <div className="px-3 pt-3 pb-2">
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-gold-400 font-bold">
              {t('restaurants.nav.for_new_restaurants', 'For New Restaurants')}
            </p>
            <p className="text-[12px] text-white/45 mt-0.5">
              {t(
                'restaurants.nav.tagline',
                'Everything you need to launch on NoFee.',
              )}
            </p>
          </div>
          <div className="h-px bg-white/[0.06] mx-2" />

          <div className="py-1">
            {ITEMS.map((it) => {
              const Icon = it.icon;
              return (
                <button
                  key={it.label}
                  role="menuitem"
                  type="button"
                  onClick={() => handleSelect(it)}
                  data-testid={it.testId}
                  className="w-full text-left flex items-start gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.04] focus:bg-white/[0.05] outline-none transition-colors group"
                >
                  <span className="w-9 h-9 rounded-xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center flex-shrink-0 group-hover:border-gold-500/40 transition-colors">
                    <Icon className="w-4 h-4 text-gold-300" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[14px] font-semibold text-white">
                      {it.label}
                    </span>
                    <span className="block text-[12px] text-white/55 leading-snug mt-0.5">
                      {it.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Section 2 — Already a NoFee Restaurant? */}
          <div className="px-3 pt-3 pb-3 border-t border-white/[0.06] mt-1">
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-gold-400 font-bold mb-2">
              {t(
                'restaurants.nav.already_restaurant',
                'Already a NoFee Restaurant?',
              )}
            </p>
            <button
              type="button"
              onClick={handleSignIn}
              data-testid="restaurants-dropdown-item-signin"
              className="group w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[13.5px] tracking-tight transition-all shadow-[0_12px_32px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
            >
              <LogIn className="w-4 h-4" />
              {t('restaurants.nav.sign_in', 'Sign In')}
            </button>
            <p className="text-[11.5px] text-white/45 text-center mt-2">
              {t(
                'restaurants.nav.sign_in_desc',
                'Access your restaurant dashboard.',
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
